import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Typography,
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TextField,
    CircularProgress,
    Alert,
    Button,
    Pagination,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TableSortLabel,
    Box
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import styled from "styled-components";
import dayjs from "dayjs";
import { functions } from "../services/firebase";
import { MobileCard } from "../components/OSMDataAdmin/styled";
import { captureError } from "../lib/errorReporting";
import { useIsMobile } from "../hooks/useIsMobile";

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export function Blacklist() {
    const listCallable = useMemo(() => functions.httpsCallable("listBlacklistedVenues"), []);
    const deleteCallable = useMemo(() => functions.httpsCallable("deleteBlacklistedVenue"), []);
    const isMobile = useIsMobile();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [sortField, setSortField] = useState("name");
    const [sortDirection, setSortDirection] = useState("asc");

    useEffect(() => {
        const handle = setTimeout(() => {
            setSearchTerm(searchInput.trim());
            setPage(1);
        }, 350);
        return () => clearTimeout(handle);
    }, [searchInput]);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await listCallable({
                page,
                pageSize,
                search: searchTerm,
                sortField,
                sortDirection
            });
            const { rows: dataRows, total: totalRows } = response?.data || {};
            setRows(dataRows || []);
            setTotal(Number.isFinite(totalRows) ? totalRows : dataRows?.length || 0);
        } catch (err) {
            captureError(err, { view: "Blacklist", action: "loadExcludedVenues" });
            setError(err?.message || "Failed to load excluded venues");
        } finally {
            setLoading(false);
        }
    }, [listCallable, page, pageSize, searchTerm, sortField, sortDirection]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const pageCount = useMemo(() => {
        const count = Math.ceil((total || 0) / pageSize);
        return Math.max(1, count || 1);
    }, [pageSize, total]);

    useEffect(() => {
        if (page > pageCount) {
            setPage(pageCount);
        }
    }, [page, pageCount]);

    const formatDate = (value) => {
        if (!value) return "";
        const parsed = value?.toDate ? dayjs(value.toDate()) : dayjs(value);
        return parsed.isValid() ? parsed.format("MMM D, YYYY h:mm A") : "";
    };

    const handleSortChange = (field) => {
        setSortField((current) => {
            if (current === field) {
                setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"));
                return current;
            }
            setSortDirection("asc");
            return field;
        });
        setPage(1);
    };

    const renderSortLabel = (label, field) => (
        <TableSortLabel
            active={sortField === field}
            direction={sortDirection}
            onClick={() => handleSortChange(field)}
            hideSortIcon={false}
            IconComponent={(props) => (
                <ExpandMoreRoundedIcon
                    {...props}
                    fontSize="small"
                    sx={{
                        ml: 0.25,
                        transition: "transform 120ms ease",
                        transform: sortDirection === "desc" ? "rotate(0deg)" : "rotate(180deg)"
                    }}
                />
            )}
        >
            {label}
        </TableSortLabel>
    );

    const handleDelete = async () => {
        if (!deleteTarget) {
            return;
        }
        setDeleting(true);
        setError("");
        try {
            await deleteCallable({ id: deleteTarget.id });
            // Optimistically refresh data
            if (rows.length === 1 && page > 1) {
                setPage((prev) => Math.max(1, prev - 1));
            } else {
                await loadData();
            }
            setDeleteTarget(null);
            setTotal((prev) => Math.max(0, prev - 1));
        } catch (err) {
            captureError(err, { view: "Blacklist", action: "deleteExcludedVenue" });
            setError(err?.message || "Failed to delete excluded venue");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <PageContainer>
            <HeaderRow>
                <div>
                    <Typography variant="body2" color="textSecondary">
                        These are the venues that have been rejected from OSM and/or No Bar Left Behind lists. <br />
                        Removing a venue from this list will allow it to be re-added by these above listed automation flows. <br />
                        Removing a venue from this list does not automatically add them to bars.
                    </Typography>
                </div>
                <Typography variant="body2" color="textSecondary">
                    Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total || 0)} of {total || 0}
                </Typography>
            </HeaderRow>

            {error ? <Alert severity="error">{error}</Alert> : null}

            <Filters>
                <TextField
                    label="Search by name or address"
                    variant="outlined"
                    size="small"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    fullWidth
                    InputProps={{
                        placeholder: "Start typing to filter"
                    }}
                />
            </Filters>

            <Paper sx={{ padding: isMobile ? 1 : 2 }}>
                {loading ? (
                    <LoadingWrap>
                        <CircularProgress size={28} />
                    </LoadingWrap>
                ) : isMobile ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                        {rows.length === 0 ? (
                            <Typography variant="body2" color="textSecondary">
                                No excluded venues found.
                            </Typography>
                        ) : (
                            rows.map((row) => (
                                <MobileCard key={row.id}>
                                    <div className="card-row">
                                        <span className="card-label">Name</span>
                                        <span className="card-value" style={{ fontWeight: 600 }}>{row.name}</span>
                                    </div>
                                    <div className="card-row">
                                        <span className="card-label">Street</span>
                                        <span className="card-value">{row.address_1 || ""}</span>
                                    </div>
                                    <div className="card-row">
                                        <span className="card-label">City</span>
                                        <span className="card-value">{row.city || ""}</span>
                                    </div>
                                    <div className="card-row">
                                        <span className="card-label">State</span>
                                        <span className="card-value">{row.state || ""}</span>
                                    </div>
                                    <div className="card-row">
                                        <span className="card-label">Zip</span>
                                        <span className="card-value">{row.zip || ""}</span>
                                    </div>
                                    <div className="card-row">
                                        <span className="card-label">Created</span>
                                        <span className="card-value">{formatDate(row.created_at)}</span>
                                    </div>
                                    <Box mt={1}>
                                        <Button
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            fullWidth
                                            startIcon={<BlockIcon />}
                                            onClick={() => setDeleteTarget(row)}
                                        >
                                            Remove
                                        </Button>
                                    </Box>
                                </MobileCard>
                            ))
                        )}
                    </div>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{renderSortLabel("Name", "name")}</TableCell>
                                <TableCell>{renderSortLabel("Street", "street")}</TableCell>
                                <TableCell>{renderSortLabel("City", "city")}</TableCell>
                                <TableCell>{renderSortLabel("State", "state")}</TableCell>
                                <TableCell>{renderSortLabel("Zip", "zip")}</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                    {renderSortLabel("Created At", "created")}
                                </TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <Typography variant="body2" color="textSecondary">
                                            No excluded venues found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((row) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{row.address_1 || ""}</TableCell>
                                        <TableCell>{row.city || ""}</TableCell>
                                        <TableCell>{row.state || ""}</TableCell>
                                        <TableCell>{row.zip || ""}</TableCell>
                                        <TableCell>{formatDate(row.created_at)}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                startIcon={<BlockIcon />}
                                                onClick={() => setDeleteTarget(row)}
                                            >
                                                Remove
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                )}
            </Paper>

            <PaginationRow>
                <div className="rows-per-page">
                    <span className="label">Rows per page</span>
                    <Select
                        size="small"
                        value={pageSize}
                        onChange={(e) => {
                            const next = Number(e.target.value) || PAGE_SIZE_OPTIONS[0];
                            setPageSize(next);
                            setPage(1);
                        }}
                    >
                        {PAGE_SIZE_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </div>
                <div className="page-controls">
                    <Typography variant="body2" color="textSecondary" className="page-summary">
                        {total === 0
                            ? "No results"
                            : `Showing ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, total)} of ${total}`}
                    </Typography>
                    <Pagination
                        count={pageCount}
                        page={page}
                        onChange={(_, val) => setPage(val)}
                        color="primary"
                        size="small"
                        shape="rounded"
                        showFirstButton
                        showLastButton
                        siblingCount={1}
                        boundaryCount={1}
                    />
                </div>
            </PaginationRow>

            <Dialog
                open={Boolean(deleteTarget)}
                onClose={() => (deleting ? null : setDeleteTarget(null))}
                aria-labelledby="delete-blacklist-dialog-title"
                aria-describedby="delete-blacklist-dialog-description"
            >
                <DialogTitle id="delete-blacklist-dialog-title">Delete excluded venue?</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-blacklist-dialog-description">
                        This will remove the venue from the excluded list and allow it to be re-added by automation flows.
                    </DialogContentText>
                    {deleteTarget ? (
                        <DialogContentText sx={{ mt: 1 }}>
                            <strong>Name:</strong> {deleteTarget.name || "Unknown"}
                            <br />
                            <strong>Address:</strong> {[deleteTarget.address_1, deleteTarget.city, deleteTarget.state, deleteTarget.zip].filter(Boolean).join(", ") || deleteTarget.address || ""}
                        </DialogContentText>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button color="error" onClick={handleDelete} disabled={deleting}>
                        {deleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}

const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
`;

const Filters = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const LoadingWrap = styled.div`
    display: flex;
    justify-content: center;
    padding: 16px 0;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const PaginationRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 12px 0;
    border-top: 1px solid #e0e0e0;

    .rows-per-page {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 600;
    }

    .page-controls {
        display: flex;
        align-items: center;
        gap: 12px;
        justify-content: flex-end;
        flex: 1;
        min-width: 260px;
        flex-wrap: wrap;
    }

    .page-summary {
        white-space: nowrap;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;

        .rows-per-page {
            justify-content: center;
        }

        .page-controls {
            justify-content: center;
            min-width: unset;
            flex-direction: column;
            align-items: center;
        }
    }
`;
